<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import {
  findComponentByType,
  COMPONENT_TYPES,
} from 'dashboard/helper/templateHelper';
import WhatsAppBubblePreview from 'dashboard/components-next/whatsapp/WhatsAppBubblePreview.vue';

const props = defineProps({
  campaign: { type: Object, default: null },
});

const { t } = useI18n();
const labels = useMapGetter('labels/getLabels');

const formatDate = timestamp => {
  if (!timestamp) return '-';
  const date = new Date(
    typeof timestamp === 'number' ? timestamp * 1000 : timestamp
  );
  return date.toLocaleString();
};

const templateName = computed(
  () => props.campaign?.template_params?.name || '-'
);
const templateCategory = computed(
  () => props.campaign?.template_params?.category || '-'
);
const templateLanguage = computed(
  () => props.campaign?.template_params?.language || '-'
);

const audienceLabel = computed(() => {
  const campaign = props.campaign;
  if (!campaign) return '-';

  if (campaign.audience_type === 'csv_import') {
    const filename = campaign.campaign_import?.filename;
    return filename ? `CSV: ${filename}` : 'CSV Import';
  }

  const audienceIds = (campaign.audience || [])
    .filter(a => a.type === 'Label')
    .map(a => a.id);

  if (audienceIds.length === 0) return '-';

  const labelNames = audienceIds
    .map(id => labels.value?.find(l => l.id === id)?.title)
    .filter(Boolean);

  return labelNames.length > 0 ? labelNames.join(', ') : '-';
});

const excludedLabelsText = computed(() => {
  const excludedIds = props.campaign?.trigger_rules?.excluded_labels;
  if (!excludedIds || excludedIds.length === 0) return '';

  const names = excludedIds
    .map(id => labels.value?.find(l => l.id === id)?.title)
    .filter(Boolean);

  return names.length > 0 ? names.join(', ') : '';
});

const importSummary = computed(() => {
  const imp = props.campaign?.campaign_import;
  if (!imp) return null;
  return imp;
});

const templateComponents = computed(() => {
  const components = props.campaign?.template_params?.components;
  if (!components || !Array.isArray(components)) return null;
  return { components };
});

const templateHeader = computed(() =>
  templateComponents.value
    ? findComponentByType(templateComponents.value, COMPONENT_TYPES.HEADER)
    : null
);

const templateFooter = computed(() =>
  templateComponents.value
    ? findComponentByType(templateComponents.value, COMPONENT_TYPES.FOOTER)
    : null
);

const templateButtons = computed(() => {
  if (!templateComponents.value) return [];
  const comp = findComponentByType(
    templateComponents.value,
    COMPONENT_TYPES.BUTTONS
  );
  return comp?.buttons || [];
});

const metaItems = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.CAMPAIGN_TYPE'),
    value: 'Broadcast',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.MESSAGE_TYPE'),
    value: 'Template',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.TEMPLATE'),
    value: templateName.value,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.LANGUAGE'),
    value: templateLanguage.value,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.CATEGORY'),
    value: templateCategory.value,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.AUDIENCE'),
    value: audienceLabel.value,
  },
  ...(excludedLabelsText.value
    ? [
        {
          label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.EXCLUDED_LABELS'),
          value: excludedLabelsText.value,
        },
      ]
    : []),
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.CREATED'),
    value: formatDate(props.campaign?.created_at),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.STARTED'),
    value: formatDate(props.campaign?.started_at),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.ANALYTICS.META.COMPLETED'),
    value: formatDate(props.campaign?.completed_at),
  },
]);
</script>

<template>
  <div class="h-full">
    <div v-if="campaign" class="flex flex-col gap-4 p-4 overflow-y-auto h-full">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.META.TITLE') }}
      </h3>

      <!-- Meta fields -->
      <div class="flex flex-col gap-3">
        <div
          v-for="item in metaItems"
          :key="item.label"
          class="flex flex-col gap-0.5"
        >
          <span
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wide"
          >
            {{ item.label }}
          </span>
          <span class="text-xs text-n-slate-12">{{ item.value }}</span>
        </div>
      </div>

      <!-- Import summary (CSV campaigns only) -->
      <div
        v-if="importSummary"
        class="p-3 rounded-lg bg-n-alpha-2 flex flex-col gap-1"
      >
        <span
          class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wide"
        >
          {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.META.IMPORT_SUMMARY') }}
        </span>
        <div class="flex flex-col gap-0.5 text-xs text-n-slate-12">
          <span>
            {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.META.IMPORT_PROCESSED') }}:
            {{ importSummary.processed_rows || 0 }}
          </span>
          <span v-if="importSummary.failed_rows > 0" class="text-n-ruby-11">
            {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.META.IMPORT_FAILED') }}:
            {{ importSummary.failed_rows }}
          </span>
        </div>
      </div>

      <!-- Message preview -->
      <div class="flex flex-col gap-1 mt-2">
        <span
          class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wide"
        >
          {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.META.MESSAGE_PREVIEW') }}
        </span>
        <WhatsAppBubblePreview
          :header-type="templateHeader?.format || ''"
          :header-text="templateHeader?.text || ''"
          :body-text="campaign.message || ''"
          :footer-text="templateFooter?.text || ''"
          :buttons="templateButtons"
        />
      </div>
    </div>
  </div>
</template>
